import * as React           from "react";
import {cx}                 from "emotion";

import "./cc-integration.scss";

import {XdSentry}           from "@Common/libs/sentry/xd-sentry";
import {isFieldAiEligible}  from "@Common/logic/is-field-ai-eligible";
import {cs, cst, cstAssign} from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {fragments}          from "@Common/react/fragments";
import {Invoke}             from "@Common/react/invoke";
import {Load}               from "@Common/react/load";
import {Load2}              from "@Common/react/load2";
import {OnMounted}          from "@Common/react/on-mounted";
import {UseState}           from "@Common/react/use-state";
import {Types}              from "@Common/types";
import {isDevXd}            from "@Common/ui-components/debug/is-dev";
import {AlertModal}         from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {waitTimeout}        from "@Common/utils/wait-timeout";

import {BlockSelectingReadonlyText}   from "./block-selecting-readonly-text";
import {callCcApi}                    from "./call-cc-api";
import {CcEditor}                     from "./cc-editor/cc-editor-2";
import {CcApis3}                      from "./cc_apis/cc-apis-3";
import {checkIsRequired}              from "./check-is-required";
import {ConfigDefaultAssetManager}    from "./config-default-asset-manager";
import {createWarningsArray}          from "./create-warnings-array";
import {getAssetSources}              from "./get-asset-sources";
import {getGalleryParams}             from "./get-gallery-params";
import {getProductSurfacesWithImages} from "./get-product-surfaces-with-images";
import {importImages}                 from "./import-images";
import {logFailoverIncident}          from "./log-failover-incident";
import {NotifyFailover}               from "./notify-failover";
import {NotifyReset}                  from "./notify-reset";
import {ProofViewer}                  from "./proof-viewer/proof-viewer";
import {TextFields}                   from "./text-fields";
import {selectWarnings}               from "./tools/select-warnings";
import * as variableDataHandler       from "./tools/variable-data-handler";

export const CcIntegration = ({
/*  PROPS  ************************************************************************************************************/
    userData,
    useAdvancedEditingMode  = false,
    itemId                  = undefined,
    componentId             = undefined,
    alwaysEdit              = false,
    inputData               = undefined,
    ccValues                = undefined,
    pickpackOptions         = undefined,
    pickpackStyles          = undefined,
    templateVariations      = undefined,
    distributionType        = undefined,
    qrCodeLineItemValue     = undefined,
    qrCodeUrlValue          = undefined,
    next,
}:{
/*  PROP TYPES  *******************************************************************************************************/
    userData                : any,
    useAdvancedEditingMode ?: boolean,
    itemId                 ?: string,
    componentId            ?: string,
    alwaysEdit             ?: boolean,
    inputData              ?: any,
    ccValues               ?: any,
    pickpackOptions        ?: any,
    pickpackStyles         ?: any,
    templateVariations     ?: any,
    distributionType       ?: string,
    qrCodeLineItemValue    ?: string,
    qrCodeUrlValue         ?: string
    next                    : any,
}) => cst<{
/*  CS ASSIGNMENT NODE TYPES  *****************************************************************************************/
    systemConstants         : any,
    apis                    : Types.Apis.Loaders.XpressoLoadApis,
    ccApis                  : Types.Apis.Loaders.CustomersCanvasApis,
    ccHostResponse          : any,
    templateOptions         : any,
    userLevels              : any,
    isInternalUser          : boolean,
    changed                 : Types.Common.React.UseState.UseStateNext<number>,
    lastSave                : Types.Common.React.UseState.UseStateNext<number>,
    warningWasDisplayed     : Types.Common.React.UseState.UseStateNext<boolean>,
    qrFields                : Types.Common.React.UseState.UseStateNext<any>,
    alertModal              : any,
    editing                 : any,
    ccConfig                : any,
    notifyFailover          : any,
    notifyReset             : any,
    hasUnsavedChange        : boolean,
    ccEditor                : Types.Common.Tools.CcIntegration.Models.CcEditor,
    callCcApi               : any,
    isRequired              : ({ccConfig, items}:{ccConfig:any, items:any}) => Promise<boolean>,
    proofImages             : any,
    loadHighResProducts     : any,
    highResProducts         : any,
    assetSources            : any,
    warnings                : any[],
    allowedGalleryTabs      : string[],
    mergeFields             : any,
    resetting               : Types.Common.React.UseState.UseStateNext<boolean>,
}>(
    consumeContext("systemConstants"),
    consumeContext("apis"),
    consumeContext("ccApis"),
    consumeContext("ccHostResponse"),
    consumeContext("templateOptions"),
    consumeContext("userLevels"),
    cstAssign("isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)),
    cstAssign("changed", (_, next) => UseState({next})),
    cstAssign("lastSave", (_, next) => UseState({next})),
    cstAssign("warningWasDisplayed", (_, next) => UseState({next})),
    cstAssign("qrFields", (_, next) => UseState({next})),
    cstAssign("alertModal", ({}, next) => AlertModal({next})),
    cstAssign("editing", (_, next) => alwaysEdit ? (
        next({
            value: true,
            onChange: () => {},
        })
    ) : (
        UseState({
            next,
        })
    )),
    cstAssign("ccConfig", ({ccApis}, next) => Load({
        _key: userData && JSON.stringify(userData.templateIds),
        fetch: async () => {
            let ccConfig = userData && await ccApis.getTemplateConf({...userData, itemId});
            if( ccValues?.userCreatedCcConfig ) {
                ccConfig.templateConfigurations = {...ccValues?.userCreatedCcConfig?.xdJson?.templateConfigurations}
                ccConfig.templateBindingData = {...ccValues?.userCreatedCcConfig?.xdJson?.templateBindingData}
            }
            return ccConfig;
        },
        next,
    })),
    cstAssign("notifyFailover", ({ccHostResponse}, next) => NotifyFailover({ccHostResponse, next})),
    cstAssign("notifyReset", ({}, next) => NotifyReset({next})),
    cstAssign("hasUnsavedChange", ({changed, lastSave}, next) => next(changed.value == null ? false : lastSave.value == null ? true : (changed.value > lastSave.value))),
    cstAssign("ccEditor", ({ccApis, ccHostResponse, editing, apis, notifyFailover, notifyReset, ccConfig, systemConstants}, next) => <CcEditor {...{
        apis,
        ccApis,
        ccHostResponse,
        useAdvancedEditingMode,
        ccConfig,
        systemConstants: systemConstants.value,
        inputData,
        itemId,
        componentId,
        editing,
        pickpackOptions,
        pickpackStyles,
        templateVariations,
        distributionType,
        qrCodeLineItemValue,
        qrCodeUrlValue,
        onNotifyFailover: notifyFailover,
        onNotifyReset: notifyReset,
        logFailoverIncident: ({retryHost, exception}) => logFailoverIncident({ccHost: ccHostResponse.value.ccHost, retryHost, exception}),
        next,
    }}/>),
    cstAssign("callCcApi", ({ccHostResponse, ccEditor, notifyFailover, systemConstants, templateOptions}, next) => next((fn, args) => callCcApi(fn, args, {
        onNotifyFailover: notifyFailover,
        logFailoverIncident: ({retryHost, exception}) => logFailoverIncident({ccHost: ccHostResponse.value.ccHost, retryHost, exception}),
        ccHost: ccHostResponse.value.ccHost,
        ccVersion: ccEditor.ccVersion,
        getCcHost: async () => {
            // return the current host if already set
            if (ccHostResponse?.ccHost && ccHostResponse?.ccVersion) return ccHostResponse;
            // query host from CCApi if does not already exist
            const ccApi = CcApis3({host: systemConstants.value.DS_SERVER_URL});
            return await ccApi.getHost({
                templateId: templateOptions?.sku.front,
                stateId: ccEditor.ccStateId
            });
        }
    }))),
    cstAssign("isRequired", ({ccEditor, changed, ccConfig}, next) => Load({
        _key: JSON.stringify([ccEditor.initialized, changed.value]),
        fetch: async () => {
            if (!ccEditor.initialized) {
                return null;
            }
            return await checkIsRequired({ccConfig, items: await ccEditor.getAllItems()});
        },
        next,
    })),
    cstAssign("proofImages", ({ccEditor, lastSave, callCcApi, editing, ccApis, ccConfig}, next) => Load2({
        _key: JSON.stringify([ccEditor.initialized, ccEditor.initializeError, lastSave.value]),
        // keepOutdatedValue: true,
        fetch: async () => {
            if (ccEditor.initializeError) {
                return ["assets/SPINNER_ERROR.svg"];
            }

            if (!ccEditor.initialized) {
                return null;
            }

            // await waitTimeout(5000);
            const proofImages = await callCcApi(ccApis.getLowResProducts, {
                stateId: ccEditor.ccStateId,
                surfaceCount: ccEditor.getSurfaces().length,
                userId: ccConfig.editorConfigurations.userId
            });

            // await Promise.all(proofImages.jpg.map( proofImage => fetch(proofImage, {method: "GET"} ) )); // This code looks like trying to validate the proofImages, which seems redundant

            isDevXd() ? console.log("proofImages", proofImages?.jpg) : null;
            return proofImages?.jpg;
        },
        next,
    })),
    cstAssign("loadHighResProducts", ({ccEditor, callCcApi, ccApis, ccConfig}, next) => next(async () => {
        const highResProducts = await callCcApi(ccApis.getHighResProducts, {
            stateId: ccEditor.ccStateId,
            userId: ccConfig.editorConfigurations.userId,
            surfaceCount: ccEditor.getSurfaces().length,
            rotations: ccConfig.rotations,
        });

        isDevXd() ? console.log("highResProducts", highResProducts) : null;
        return highResProducts;
    })),
    cstAssign("highResProducts", ({loadHighResProducts, ccEditor, lastSave}, next) => Load({
        _key: JSON.stringify([ccEditor.initialized, lastSave.value]),
        fetch: async () => {
            if (!ccEditor.initialized) {
                return null;
            }

            return await loadHighResProducts();
        },
        next,
    })),
    cstAssign("assetSources", ({ccConfig}, next) => next(ccConfig && getAssetSources(ccConfig, userData))),
    ({ccConfig, ccEditor, assetSources}, next) => fragments(
        next(),
        userData && ccConfig && OnMounted({
            action: () => {
                ccEditor.initialize({
                    userData, ccConfig, assetSources
                });
            },
        }),
    ),
    ({ccEditor, ccConfig, editing, qrFields}, next) => Load({
        _key: editing.value + "|" + ccEditor.currentSide + '|' + ccConfig?.templateBindingData,
        fetch: () => qrFields.onChange((!editing.value ? null : ccEditor.getQrFields({
            ccConfig, qrCodeLineItem: userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.dev_key === "qr_code")
        }))),
        next,
    }),
    cstAssign("warnings", ({ccEditor, changed, ccConfig, assetSources}:{ccEditor:Types.Common.Tools.CcIntegration.Models.CcEditor,changed:any,ccConfig:any,assetSources:any}, next) => Load({
        _key: JSON.stringify([changed.value, ccEditor.initialized]),
        keepOutdatedValue: true,
        fetch: async () => {
            if (!ccEditor.initialized) {
                return null;
            }

            await waitTimeout(500);
            const product = await ccEditor.getProduct();
            const productModel = await product.getProductModel();
            let items = productModel.getAllItems();

            // set the editor function for loading warnings based upon the editor version number - this was changed with version 6
            const spEditorFunction = (ccEditor.ccVersionMajor === 5) ? "_getViolationWarningData" : "getViolationWarningData";

            // load all warnings
            return await createWarningsArray({
                warningData: await ccEditor.editorEval(`spEditor.${spEditorFunction}()`),
                items,
                getSurfaceName: (surface) => product.surfaces[surface].name,
                ccConfig,
                assetSources,
                userData,
            });
        },
        next,
    })),
    ({ccEditor, ccConfig, assetSources}, next) => fragments(
        next(),
        ccEditor.initialized && userData?.sourceData.xd.company.prefs?.customersCanvasUsesDefaultAssetManager && Invoke({
            action: () => ConfigDefaultAssetManager({
                ccEditor,
                userData,
                ccConfig,
                assetSources
            }),
        }),
    ),
    cstAssign("allowedGalleryTabs", ({apis}, next) => Load({
        _key: `galleryTabs-${userData?.sourceData?.xd?.company?.idCompany}`,
        fetch: async () => {
            if (!userData?.sourceData?.xd?.company?.idCompany) return null;

            return await apis.next.gallery.getAllowedPhotoGalleryTabs({
                idCompany: userData.sourceData.xd.company.idCompany,
                back: userData.templateIds[userData.templateIds.length-1]
            });

        },
        next,
    })),
    cstAssign("resetting", (_, next) => UseState({next})),
    ({
        editing, ccEditor, proofImages, highResProducts, loadHighResProducts, ccHostResponse, warnings, qrFields,
        changed, lastSave, hasUnsavedChange, warningWasDisplayed, ccConfig, ccApis, callCcApi, apis, isInternalUser,
        isRequired, allowedGalleryTabs, resetting
    }) => next({
        // Safe to go
        render: ({heightPerWidthRatio=undefined}={}) => cs(
            ["proofViewer", (_, next) => ProofViewer({
                currentImgIndex: ccEditor.currentSide,
                next,
            })],
            ({proofViewer}) => (
                <div
                    className="cc-integration cc-integration-5tc"
                    //xd-qa-type="canvas"
                    style={!ccEditor.fullScreen ? {
                        // effected apm2 distorted low res image paddingTop: "70%"
                        paddingTop: heightPerWidthRatio != null ? (
                            (heightPerWidthRatio * 100) + "%"
                        ) : proofViewer.size ? (
                            `${Math.round(proofViewer.size.height / proofViewer.size.width * 100 * 100)/100}%`
                        ) : (
                            `50%`
                        )
                    } : {
                        position: "fixed",
                        top: "7.5%",
                        left: "5%",
                        width: "90%",
                        height: "85%",
                        zIndex: 101,
                    }}
                >

                    {ccEditor.fullScreen && (
                        <button
                            className="fullScreenButton-42j"
                            onClick={ccEditor.closeFullScreenMode}
                        >
                            Close Fullscreen
                        </button>
                    )}

                    <div className={cx("iframe-wrapper", {show: editing.value})}>
                        {/*
                            proofViewer isn't used in xpresso main flow, but this call makes it call for the jpg images.
                            This is an issue with multipage templates, slowing down each step.
                            heightPerWidthRatio is defined when on xpresso main flow.
                        */}
                        {!heightPerWidthRatio && proofViewer.render({
                            visible: !editing.value,
                            images: proofImages.value,
                        })}
                        <iframe ref={ccEditor.iframeRef} data-hj-allow-iframe=''/>
                    </div>

                </div>
            )
        ),

        // Safe to go
        renderBlockSelectingReadonlyText: ({onSelect}) => ccEditor.initialized && (
            BlockSelectingReadonlyText({onSelect, ccEditor})
        ),

        registerCustomGallery: ({onOpen}) => cs(
            ({}, next) => !ccEditor.initialized ? null : next(), // continue if ccEditor has initialized
            ({}, next) => !userData?.sourceData.xd.company.prefs?.customersCanvasUsesDefaultAssetManager ? next() : null, // continue if uses photo dashboard
            ({}, next) => !itemId ? null : next(), // continue if an order ID exists
            () => Invoke({
                props: async () => {

                    // load gallery params and return if no image area found
                    const galleryParams = await getGalleryParams({
                        ccEditor,
                        userData,
                        userCreatedFieldSettings: ccValues?.userCreatedCcConfig?.ccJson?.frames,
                        allowedGalleryTabs,
                        adminTool: apis.next.isAdminTool
                    });
                    if (!galleryParams) return;

                    // open Photo Dashboard if an image area was found
                    onOpen({
                        ...galleryParams,
                        templateId: userData?.templateIds?.[userData.templateIds.length-1],
                        orderId: parseInt(itemId),
                        photoServiceEmail: userData.sourceData.xd.user?.agentEmail,
                        listing: userData.sourceData.xd.listing,
                        adminTool: apis.next.isAdminTool
                    });

                },
                action: ({getLatestProps}) => {
                    const CustomersCanvas = window["CustomersCanvas"] || null;
                    ccEditor.subscribe(
                        // @ts-ignore
                        CustomersCanvas.IframeApi.PostMessage.Events.AssetManagerOpened,
                        async () => {
                            const action = getLatestProps();
                            action();
                        },
                    );

                    // don't allow the editor to display the default asset manager if using the custom gallery
                    ccEditor.hideDefaultAssetManager();
                },
            }),
        ),

        // Safe to go
        loadTextFields: ({side, next}) => cs(
            () => TextFields({
                ccEditor, ccConfig, userData, next, side,
                onChanged: () => changed.onChange(Date.now()),
            }),
        ),

        ccEditor, // Allowing to bypass this "cc-integration" component, future plan: no more "cc-integration"


        initialized: ccEditor.initialized,
        editor: ccEditor.editor,
        editing: editing.value,
        changed: hasUnsavedChange,
        fullScreen: ccEditor.fullScreen,
        setEditing: async (editing1) => {
            if (alwaysEdit) {
                return;
            }

            editing.onChange(editing1);
            // if (!editing1 && changed.value) {
            //     lastSave.onChange(Date.now());
            // }
        },
        clearChanged: async () => {
            changed.onChange(null);
            lastSave.onChange(null);
        },
        warningWasDisplayed: warningWasDisplayed.value,
        setWarningWasDisplayed: async (val) => {
            warningWasDisplayed.onChange(val);
            // if (!val && changed.value) {
            //     changed.onChange(null);
            // }
        },

        selectWarning: async () => {
            await selectWarnings(ccEditor, warningWasDisplayed);
        },

        selectField: async (side, selection) => {
            editing.onChange(true);
            ccEditor.changeSide(side, true);
            ccEditor.editor.selectItems(selection);
        },

        saveProductModel: async () => {
            await ccEditor.saveProductModel();
        },

        getWarnings: () => {
            // return ccEditor.getWarnings();
            return warnings ?? [];
        },

        // setWarnings: async () => {
        //     await ccEditor.setWarnings();
        // },

        applyQrImage:  async (qrField, imageUrl)=> await ccEditor.applyQrImage(qrField, imageUrl),

        setQrValue: ( currentSide, fieldName, valueName, text ) => {
            ccEditor.setQrValue( currentSide, fieldName, valueName, text );
        },

        setTrackingValue: ( trackingObj ) => {
            ccEditor.setTrackingValue( trackingObj );
        },

        setStyle: async ( currentSide, style, styleName, optionName ) => {
            changed.onChange(Date.now());
            await ccEditor.setStyle( currentSide, style, styleName, optionName );
        },

        applyTheme: async (themeName, isColorSet = false) => {
            changed.onChange(Date.now());
            await ccEditor.applyTheme(themeName, isColorSet);
        },

        loadWatermarkPdf: async () => {
            if (hasUnsavedChange) {
                await ccEditor.saveProduct({ccConfig});
                lastSave.onChange(Date.now());
            }
            const mergeFields = await variableDataHandler.getUsedMergeFields(
                await apis.next.templateAdmin.getVariableDataMergeFields(),
                ccEditor
            )
            let mergeData = [];
            for (const mergeField of mergeFields) {
                mergeData.push({
                    name: mergeField['tagName'].replace(/[{}]/g, ''),
                    type: mergeField['type'],
                    value: mergeField['defaultValueProof'],
                });
            }
            const watermarkPdf = (await callCcApi(ccApis.getWatermarkPdf, {
                stateId: ccEditor.ccStateId,
                userId: ccConfig.editorConfigurations.userId,
                surfaceCount: ccEditor.getSurfaces().length,
                mergeFields: mergeData,
            }))?.pdf;

            isDevXd() && console.log("cc-integration watermarkPdf", watermarkPdf);
            return watermarkPdf;
        },

        loadProductModel: async () => {
            await ccEditor.loadProductModel();
            changed.onChange(null);
            lastSave.onChange(Date.now());
        },

        save: async () => {
            try {
                // await ccEditor.clearProofImages();
                await ccEditor.saveProduct({ccConfig});
                lastSave.onChange(Date.now());
                return true;

                /*
                if (systemConstants.value?.["CC_NEW_FILE_CHECK"] !== 'true') {
                    return true;
                }
                else {
                    // await ccEditor.loadHighResProducts({ccConfig});
                    return true;
                    // const urls = await ccEditor.loadHighResProducts({ccConfig});
                    // return urls?.pdf?.map((url) => ({url})) ?? [];
                }
                */

            }
            catch(err) {
                isDevXd() ? console.log("err", err) : null;
                return
            }
        },

        // Why this is not used anywhere?
        getMergeFieldErrors: async () => variableDataHandler.getInvalidMergeFieldsFromEditor(
            await apis.next.templateAdmin.getVariableDataMergeFields(),
            ccEditor
        ),

        // getMergeFieldErrorMessageJsx: async () => {
        //     ... // Moved to higher components (cc-component, APM2 edit-form)
        // },

        // loads all surfaces with containing image areas
        // For import modal
        getProductSurfacesWithImages: async () => {
            try {
                // save the state if anything changed
                if (hasUnsavedChange) {
                    await ccEditor.saveProductState({ccConfig});
                }

                const product = await ccEditor.getProduct();
                const productModel = await product.getProductModel();

                // load proof images - will provide a visual representation of each surface
                const proofImagesJpgs = (await loadHighResProducts())?.jpg; // @@@

                if (proofImagesJpgs?.length !== productModel._surfaces.length) throw ("Proof image count does not match surface count.");

                // load all tags
                const productTags = await product.getTags();

                return await getProductSurfacesWithImages({
                    productModel, ccHostResponse: ccHostResponse.value,
                    productTags, ccConfig, proofImagesJpgs,
                });

            } catch (e) {
                if (isDevXd()) console.log("CcEditor.getProductSurfacesWithImages error", e);

                XdSentry.addBreadcrumb({
                    category: "custcanv",
                    message: "Error loading product surfaces with images from the CC editor.",
                    level: XdSentry.Severity.Error,
                    data: e
                });
                XdSentry.captureMessage("CC Editor Data Load Error", XdSentry.Severity.Log);

                return null;
            }
        },

        // Safe to go
        importImages: async (surfaceData) => importImages({ccEditor, surfaceData}),


        loadProdFiles: async () => {
            if (hasUnsavedChange) {
                await ccEditor.saveProduct({ccConfig});
                lastSave.onChange(Date.now());
            }
            const highResProducts = await loadHighResProducts();

            isDevXd() && console.log("highResProducts", highResProducts);
            return highResProducts;
        },

        // Safe to go
        surfaceHasAiFields: async () => {
            if (!ccEditor.initialized) return null; // continue if ccEditor has initialized
            try {
                const product = await ccEditor?.getProduct();
                const productModel = await product?.getProductModel();
                const fields = productModel?.getAllItems()?.filter(field => field.type === "BoundedTextItem" || field.type === "PathBoundedTextItem");
                const templateBindingData = ccConfig?.templateBindingData;
                const currentSide = ccEditor.surfaces && ccEditor.currentSide != null && ccEditor.surfaces[ccEditor.currentSide].name;
                const conatainsAiEligibleFields = fields?.length && fields?.some((textField) => {
                    const fieldTemplateSide = textField._parentContainer?.parentComponent?._name;
                    if (fieldTemplateSide === currentSide) {
                        return isFieldAiEligible(templateBindingData, textField);
                    }
                    else return false;
                });
                return conatainsAiEligibleFields;
            } catch (err) {
                isDevXd() && console.log("err", err);
                return false;
            }
        },
        setResetting: async (val) => resetting.onChange(val),
        sides: ccEditor.surfaces?.map((surface) => surface.name),
        currentSide: ccEditor.surfaces && ccEditor.currentSide != null && ccEditor.surfaces[ccEditor.currentSide].name,
        currentSideIndex: ccEditor.surfaces && ccEditor.currentSide,
        changeSide: (sideIndex) => ccEditor.changeSide(sideIndex, editing.value),
        qrFields: qrFields.value,
        isRequired,
        proofImages: proofImages.loading ? null : proofImages.value,
        ccHost: ccHostResponse.value.ccHost,
        ccStateId: resetting.value ? null: ccEditor.ccStateId,
        ccVersion: ccEditor.ccVersion,
        highResProducts,
        override: ccEditor.override,
        ccConfig,
        qrCodeUrlValue,
        sourceData: userData?.sourceData ?? {},
        bindingData: ccConfig?.templateBindingData,
        inputData: ccEditor.inputData,
        pickpackOptions: ccEditor.pickpackOptions,
        pickpackStyleSelections: ccEditor.pickpackStyleSelections,
        openFullScreenMode: ccEditor.openFullScreenMode,
    })
);
